import { Table, TableHeader, TableRow } from 'component/Table';
import React, { useState, useRef, useEffect } from 'react';
import { useUser } from 'routes/UserContext';
import { Link } from 'react-router-dom';
import { ReactComponent as PencilSvg } from 'assets/images/icons/mode_edit_24px.svg';
import { ReactComponent as ViewSvg } from 'assets/images/icons/Eye-on.svg';
import { ReactComponent as CommentIcon } from 'assets/images/icons/chat.svg';
import { DeleteReportDialog } from 'component/Dialog';
import { exportEOREDocx } from 'utils/Helper';
import { InteractiveValidationStatus } from 'component/InteractiveValidationStatus';
import { ReactComponent as ExportIcon } from 'assets/images/icons/export-icon.svg';
import useReportActions from 'hooks/useReportActions';
import { EORE_HEADERS } from 'consts/ReportHeaders';
import { ReactComponent as SortByIcon } from 'assets/images/icons/Sort-by2.svg';
import { getValidationStatuses } from 'api/FilterApi';
import {
  getEoreTeamChoices,
  getEoreLocalCommunityChoices,
  getEoreSettlementChoices,
  getEorePlaceTypeChoices,
  getEoreTypeOfSessionChoices
} from 'api/ChoicesApi';

import { AsyncPaginate } from 'react-select-async-paginate';

export const typeOfSessionOptions = (value) => {
  switch (value) {
    case 'face_to_face':
      return 'Взаємодія віч-на-віч (сессії в школах, установах, в громадах тощо)';
    case 'online':
      return 'Онлайн сесії (онлайн сесії з вчителями, проф. працівниками в школах, установах)';
    case 'tot':
      return 'Навчання тренерів (саме тих, хто буде проводити сесії після навчання)';
    case 'materials_distr':
      return 'Розповсюдження матеріалів (як окрема діяльність)';
    default:
      return 'Інше / Other';
  }
};

export const truncateTypeOfSession = (value, maxLength = 30) => {
  const fullText = typeOfSessionOptions(value);
  if (fullText?.length <= maxLength) {
    return fullText;
  }
  return fullText?.substring(0, maxLength) + '...';
};

export const placeTypeOptions = (value) => {
  switch (value) {
    case 'educational_facility':
      return 'Школа/навчальний заклад';
    case 'organisation':
      return 'Підприємство/організація';
    case 'household_visit':
      return 'Візит в домогосподарство';
    case 'eecp':
      return 'EECP';
    case 'openair':
      return 'Сесія на відкритому повітрі';
    default:
      return 'Інше / Other';
  }
};

const FilterDropdown = ({
  isOpen,
  onClose,
  title,
  selectedValues,
  onSelectionChange,
  position,
  filterType,
  currentFilters,
  currentDates
}) => {
  const dropdownRef = useRef(null);
  const [tempSelectedValues, setTempSelectedValues] = useState(selectedValues || []);

  useEffect(() => {
    setTempSelectedValues(selectedValues || []);
  }, [selectedValues]);

  const loadOptions = async (searchQuery, loadedOptions, { page }) => {
    try {

      if (filterType === 'validation_status') {
        try {
          const query = {};

          if (currentDates?.dateFrom) query.date_from = currentDates.dateFrom;
          if (currentDates?.dateTo) query.date_to = currentDates.dateTo;

          if (currentFilters?.selectedDonor?.length > 0) {
            query.donor = currentFilters?.selectedDonor?.map(d => d.value || d);
          }
          if (currentFilters?.selectedCustomer?.length > 0) {
            query['task-customer'] = currentFilters?.selectedCustomer?.map(c => c.value || c);
          }
          if (currentFilters?.selectedTeam?.length > 0) {
            query.team_id = currentFilters?.selectedTeam?.map(t => t.value || t);
          }
          if (currentFilters?.selectedLocalCommunity?.length > 0) {
            query.local_community = currentFilters?.selectedLocalCommunity?.map(c => c.value || c);
          }
          if (currentFilters?.selectedSettlement?.length > 0) {
            query.settlement_name = currentFilters?.selectedSettlement?.map(s => s.value || s);
          }
          if (currentFilters?.selectedTypeOfPlace?.length > 0) {
            query.place_type_imsma = currentFilters?.selectedTypeOfPlace?.map(p => p.value || p);
          }
          if (currentFilters?.selectedTypeOfSession?.length > 0) {
            query.eore_type_imsma = currentFilters?.selectedTypeOfSession?.map(e => e.value || e);
          }

          const response = await getValidationStatuses('eore', query);

          let options = [];
          if (Array.isArray(response?.data) && response?.data?.length > 0) {
            const validItems = response.data.filter(item => {
              try {
                const value = item?.value ?? item;
                const label = item?.label ?? item;

                const isValidValue = value != null && typeof value === 'string' && value.trim() !== '';
                const isValidLabel = label != null && typeof label === 'string' && label.trim() !== '';

                if (!isValidValue || !isValidLabel) {
                  console.error('Invalid item:', item);
                }

                return isValidValue && isValidLabel;
              } catch (error) {
                console.error(`EORE: Error processing validation:`, item, error);
                return false;
              }
            });

            options = validItems.length > 0 ? validItems.map(item => ({
              value: item?.value || item,
              label: item?.label || item,
            })) : [{ value: 'no-data', label: 'Немає даних', isDisabled: true }];
          } else {
            options = [{ value: 'no-data', label: 'Немає даних', isDisabled: true }];
          }

          return {
            options,
            hasMore: false,
            additional: { page: 1 }
          };
        } catch (error) {
          return {
            options: [{ value: 'no-data', label: 'Немає даних', isDisabled: true }],
            hasMore: false,
            additional: { page: 1 }
          };
        }
      }

      let apiCall;
      switch (filterType) {
        case 'team_name':
          apiCall = getEoreTeamChoices;
          break;
        case 'local_community_name':
          apiCall = getEoreLocalCommunityChoices;
          break;
        case 'settlement_name':
          apiCall = getEoreSettlementChoices;
          break;
        case 'place_type_imsma':
          apiCall = getEorePlaceTypeChoices;
          break;
        case 'eore_type_imsma':
          apiCall = getEoreTypeOfSessionChoices;
          break;
        default:
          console.warn(`Unknown EORE filter type: ${filterType}`);
          return {
            options: [{ value: 'error', label: 'Немає даних', isDisabled: true }],
            hasMore: false,
            additional: { page: 1 }
          };
      }

      const params = { page };

      if (searchQuery && searchQuery?.trim()) {
        params.search = searchQuery?.trim();
      }

      const searchParams = new URLSearchParams();

      searchParams.append('page', page);
      if (searchQuery && searchQuery?.trim()) {
        searchParams.append('search', searchQuery?.trim());
      }

      if (!currentFilters && !currentDates) {
        const params = { page };
        if (searchQuery && searchQuery?.trim()) {
          params.search = searchQuery?.trim();
        }
        const response = await apiCall(params);

        let formattedData = [];
        let hasMore = false;

        if (response?.data) {
          if (Array?.isArray(response?.data?.results)) {
            formattedData = response?.data?.results?.map(item => ({
              value: item,
              label: getHumanReadableLabel(filterType, item)
            }));
            hasMore = Boolean(response?.data?.next);
          } else if (Array?.isArray(response?.data)) {
            formattedData = response?.data?.map(item => ({
              value: item,
              label: getHumanReadableLabel(filterType, item)
            }));
            hasMore = false;
          }
        }
        if (formattedData?.length === 0) {
          formattedData = [{ value: 'no-data', label: 'Немає даних', isDisabled: true }];
          hasMore = false;
        }

        return {
          options: formattedData,
          hasMore,
          additional: { page: page + 1 }
        };
      }

      if (currentDates?.dateFrom) {
        searchParams.append('date_from', currentDates?.dateFrom);
      }
      if (currentDates?.dateTo) {
        searchParams.append('date_to', currentDates?.dateTo);
      }

      if (currentFilters?.selectedDonor?.length > 0 && filterType !== 'donor') {
        currentFilters?.selectedDonor?.forEach(donor => {
          searchParams.append('donor', donor?.value || donor);
        });
      }
      if (currentFilters?.selectedCustomer?.length > 0 && filterType !== 'customer') {
        currentFilters?.selectedCustomer?.forEach((customer) => {
          searchParams.append('task-customer', customer?.value || customer);
        });
      }
      if (currentFilters?.selectedTeam?.length > 0 && filterType !== 'team_name') {
        currentFilters.selectedTeam.forEach(team => {
          searchParams.append('team_id', team?.value || team);
        });
      }
      if (currentFilters?.selectedLocalCommunity?.length > 0 && filterType !== 'local_community_name') {
        currentFilters?.selectedLocalCommunity?.forEach(community => {
          searchParams.append('local_community', community?.value || community);
        });
      }
      if (currentFilters?.selectedSettlement?.length > 0 && filterType !== 'settlement_name') {
        currentFilters?.selectedSettlement?.forEach(settlement => {
          searchParams.append('settlement_name', settlement?.value || settlement);
        });
      }
      if (currentFilters?.selectedTypeOfPlace?.length > 0 && filterType !== 'place_type_imsma') {
        currentFilters?.selectedTypeOfPlace?.forEach(place => {
          searchParams.append('place_type_imsma', place?.value || place);
        });
      }
      if (currentFilters?.selectedTypeOfSession?.length > 0 && filterType !== 'eore_type_imsma') {
        currentFilters?.selectedTypeOfSession?.forEach(session => {
          searchParams.append('eore_type_imsma', session?.value || session);
        });
      }
      if (currentFilters?.selectedStatus?.length > 0 && filterType !== 'validation_status') {
        currentFilters?.selectedStatus?.forEach(status => {
          searchParams.append('validation_status', status?.value || status);
        });
      }

      const response = await apiCall(searchParams);

      let formattedData = [];
      let hasMore = false;

      if (response?.data) {
        if (Array?.isArray(response?.data?.results)) {
          formattedData = response?.data?.results?.map(item => ({
            value: item,
            label: getHumanReadableLabel(filterType, item)
          }));
          hasMore = Boolean(response?.data?.next);
        } else if (Array?.isArray(response?.data)) {
          formattedData = response?.data?.map(item => ({
            value: item,
            label: getHumanReadableLabel(filterType, item)
          }));
          hasMore = false;
        }
      }
      if (formattedData?.length === 0) {
        formattedData = [{ value: 'no-data', label: 'Немає даних', isDisabled: true }];
        hasMore = false;
      }

      return {
        options: formattedData,
        hasMore,
        additional: { page: page + 1 }
      };
    } catch (error) {
      return {
        options: [{ value: 'error', label: 'Немає даних', isDisabled: true }],
        hasMore: false,
        additional: { page: 1 }
      };
    }
  };

  const handleSelectChange = (selectedOptions) => {
    const filteredOptions = (selectedOptions || [])?.filter(option => !option?.isDisabled);
    setTempSelectedValues(filteredOptions);
  };

  const handleApply = () => {
    onSelectionChange(tempSelectedValues);
    onClose();
  };

  const handleCancel = () => {
    setTempSelectedValues(selectedValues || []);
    onClose();
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        handleCancel();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, selectedValues]);

  if (!isOpen) return null;

  return (
    <div
      ref={dropdownRef}
      className="filter-dropdown"
      style={{
        top: `${position?.top || 0}px`,
        left: `${position?.left || 0}px`
      }}
    >
      <div className="filter-dropdown-header">
        <h4>Filter by {title}</h4>
      </div>
      <div className="filter-dropdown-content">
        <div
          className="filter-select-wrapper"
          onClick={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
        >
          <AsyncPaginate
            isMulti
            isSearchable={!(filterType === 'place_type_imsma' || filterType === 'eore_type_imsma' || filterType === 'validation_status')}
            value={tempSelectedValues}
            loadOptions={loadOptions}
            onChange={handleSelectChange}
            additional={{ page: 1 }}
            placeholder={`Select ${title?.toLowerCase()}...`}
            className="filter-select"
            classNamePrefix="filter-select"
            cacheOptions={false}
            defaultOptions={true}
            isOptionDisabled={(option) => option.isDisabled}
            noOptionsMessage={({ inputValue }) =>
              inputValue ? "Nothing found" : "Enter text to search"
            }
            styles={{
              menu: (provided) => ({
                ...provided,
                maxHeight: 200,
              }),
              menuList: (provided) => ({
                ...provided,
                maxHeight: 200,
              }),
              option: (provided, state) => ({
                ...provided,
                backgroundColor: state?.isDisabled ? 'transparent' : provided?.backgroundColor,
                color: state?.isDisabled ? '#999' : provided?.color,
                cursor: state?.isDisabled ? 'default' : 'pointer',
                ':hover': {
                  backgroundColor: state?.isDisabled ? 'transparent' : provided[':hover']?.backgroundColor,
                },
              }),
            }}
          />
        </div>
      </div>
      <div className="filter-dropdown-actions">
        <button
          className="cancel-button"
          onClick={handleCancel}
        >
          Cancel
        </button>
        <button
          className="apply-button"
          onClick={handleApply}
        >
          Apply
        </button>
      </div>
    </div>
  );
};

const getHumanReadableLabel = (type, value) => {
  const labelMaps = {
    place_type_imsma: {
      'household_visit': 'Візит в домогосподарство',
      'other': 'Інше',
      'eecp': 'EECP',
      'organisation': 'Підприємство/організація',
      'educational_facility': 'Освітній заклад'
    },
    eore_type_imsma: {
      'face_to_face': 'Взаємодія віч-на-віч (сессії в школах, установах, в громадах тощо)',
      'online': 'Онлайн сесії (онлайн сесії з вчителями, проф. працівниками в школах, установах)',
      'tot': 'Навчання тренерів (саме тих, хто буде проводити сесії після навчання)',
      'materials_distr': 'Розповсюдження матеріалів (як окрема діяльність)'
    }
  };

  return labelMaps[type]?.[value] || value;
};

export const EOREReportsTable = (props) => {
  const [rotations, setRotations] = useState({});
  const [sortDirection, setSortDirection] = useState({});
  const [lastClickedIcon, setLastClickedIcon] = useState(null);

  const [activeFilter, setActiveFilter] = useState(null);
  const [filterPosition, setFilterPosition] = useState({ top: 0, left: 0 });
  const [filters, setFilters] = useState({
    team_name: [],
    local_community_name: [],
    settlement_name: [],
    place_type_imsma: [],
    eore_type_imsma: [],
    validation_status: []
  });

  const { user } = useUser();
  const canChangeStatus = user?.permissions?.change_settings;

  const handleStatusChange = (reportId, newStatus) => {
    if (!Array.isArray(props?.reportsData)) {
      console.error('reportsData is not an array:', props?.reportsData);
      return;
    }

    const updatedData = props?.reportsData?.map(item =>
      item?.id === reportId
        ? {
            ...item,
            validation_status: newStatus,
            status_manually_changed: true
          }
        : item
    );

    if (props?.setReportsPageList && typeof props?.setReportsPageList === 'function') {
      props?.setReportsPageList(updatedData);
    }
  };

  const resetRotations = () => {
    setRotations({});
    setSortDirection({});
    setLastClickedIcon(null);
  };

  useEffect(() => {
    if (props.onResetFilters) {
      props.onResetFilters(clearAllFilters);
    }
  }, [props.onResetFilters]);

  useEffect(() => {
    if (props.onResetRotations) {
      props.onResetRotations(resetRotations);
    }
  }, [props.onResetRotations]);

  const handleSortIconClick = async (key) => {
    try {
      props?.setReportsPageList([]);
      props?.setReportsPageCount(1);

      const currentDirection = sortDirection[key] || false;
      const newDirection = !currentDirection;
      setSortDirection(prev => ({
        ...prev,
        [key]: newDirection,
      }));

      const currentRotation = rotations[key] || 0;
      const newRotation = currentRotation + 180;

      if (lastClickedIcon && lastClickedIcon !== key) {
        setRotations(prevRotations => ({
          ...prevRotations,
          [lastClickedIcon]: 0,
        }));
      }

      setRotations(prevRotations => ({
        ...prevRotations,
        [key]: newRotation,
      }));

      setLastClickedIcon(key);

      if (props?.onSortIconClick) {
        props?.onSortIconClick(key);
      }
    } catch (error) {
      console.error('Error during sorting:', error);
    }
  };

  const handleHeaderClick = async (event, headerKey) => {
    if (!isFilterableHeader(headerKey)) return;

    event.stopPropagation();

    let headerElement = event.target;
    while (headerElement && headerElement.tagName !== 'TH') {
      headerElement = headerElement.parentElement;
    }

    if (!headerElement) return;

    let tableContainer = headerElement;
    while (tableContainer && !tableContainer.classList.contains('eore-table')) {
      tableContainer = tableContainer.parentElement;
    }

    if (!tableContainer) {
      tableContainer = document.body;
    }

    const headerRect = headerElement.getBoundingClientRect();
    const containerRect = tableContainer.getBoundingClientRect();

    const dropdownWidth = 320;
    const dropdownHeight = 300;
    const margin = 10;

    let left = headerRect.left - containerRect.left;
    let top = headerRect.bottom - containerRect.top + 4;

    if (left + dropdownWidth > containerRect.width - margin) {
      left = containerRect.width - dropdownWidth - margin;
    }
    if (left < margin) {
      left = margin;
    }

    if (headerRect.bottom + dropdownHeight > window.innerHeight - margin) {
      top = headerRect.top - containerRect.top - dropdownHeight - 4;
      if (top < margin) {
        top = margin;
      }
    }

    setFilterPosition({
      top: top,
      left: left
    });

    const newActiveFilter = activeFilter === headerKey ? null : headerKey;
    setActiveFilter(newActiveFilter);
  };

  const isFilterableHeader = (key) => {
    return ['team_name', 'local_community_name', 'settlement_name', 'place_type_imsma', 'eore_type_imsma', 'validation_status'].includes(key);
  };

  const handleFilterChange = (headerKey, selectedValues) => {
    const newFilters = {
      ...filters,
      [headerKey]: selectedValues
    };

    setFilters(newFilters);

    if (props?.onFiltersChange) {
      const mappedFilters = {
        selectedTeam: newFilters?.team_name,
        selectedLocalCommunity: newFilters?.local_community_name,
        selectedSettlement: newFilters?.settlement_name,
        selectedTypeOfPlace: newFilters?.place_type_imsma,
        selectedTypeOfSession: newFilters?.eore_type_imsma,
        selectedStatus: newFilters?.validation_status
      };

      props?.onFiltersChange(mappedFilters);
    }
  };

  const closeFilter = () => {
    setActiveFilter(null);
  };

  const clearAllFilters = () => {
    const clearedFilters = {
      team_name: [],
      local_community_name: [],
      settlement_name: [],
      place_type_imsma: [],
      eore_type_imsma: [],
      validation_status: []
    };

    setFilters(clearedFilters);

    if (props?.onFiltersChange) {
      const mappedFilters = {
        selectedTeam: [],
        selectedLocalCommunity: [],
        selectedSettlement: [],
        selectedTypeOfPlace: [],
        selectedTypeOfSession: [],
        selectedStatus: []
      };

      props?.onFiltersChange(mappedFilters);
    }
  };

  return (
    <div className="eore-table" style={{ position: 'relative' }}>
      <Table>
        <TableHeader>
          {EORE_HEADERS?.map((header, index) => (
            <th
              key={index}
              className={`text-with-icon ${header?.style ? '' : ''} ${isFilterableHeader(header?.key) ? 'filterable-header' : ''}`}
              style={{
                ...header?.style,
                cursor: isFilterableHeader(header?.key) ? 'pointer' : 'default',
                position: 'relative'
              }}
              onClick={(event) => handleHeaderClick(event, header?.key)}
            >
              {header?.key && header?.condition && header?.condition() && (
                <SortByIcon
                  className="sort-icon"
                  style={{ transform: `rotate(${rotations[header?.key] || 0}deg)` }}
                  onClick={(event) => {
                    event.stopPropagation();
                    handleSortIconClick(header?.key);
                  }}
                />
              )}
              {header?.label}
              {isFilterableHeader(header?.key) && filters[header?.key]?.length > 0 && (
                <span className="filter-badge">
                  {filters[header?.key]?.length}
                </span>
              )}
            </th>
          ))}
        </TableHeader>
        <tbody>
          {props?.reportsData?.length === 0 && !props.isLoading && (
            <TableRow>
              <td colSpan={EORE_HEADERS?.length} style={{ textAlign: 'center', padding: '20px', color: '#666' }}>
                Немає даних
              </td>
            </TableRow>
          )}
          {props?.reportsData?.map((data) => {
            return (
              <TableRow key={data?.id}>
                <td>{data?.report_date}</td>
                <td>{data?.team_id || '-'}</td>
                <td>{data?.local_community_name || data?.local_community || '-'}</td>
                <td>{data?.settlement_name || '-'}</td>
                <td>{placeTypeOptions(data?.place_type_imsma) || '-'}</td>
                <td
                  className="type-of-session-cell"
                  title={typeOfSessionOptions(data?.eore_type_imsma) || '-'}
                >
                  {truncateTypeOfSession(data?.eore_type_imsma) || '-'}
                </td>
                <td>{data?.total_beneficiaries || '-'}</td>
                <td>
                  {data?.validation_status?.value && (
                    <InteractiveValidationStatus
                      reportType="eore"
                      reportId={data?.id}
                      currentStatus={{
                        ...data?.validation_status,
                        status_manually_changed: data?.status_manually_changed
                      }}
                      onStatusChange={handleStatusChange}
                      disabled={!canChangeStatus}
                    />
                  )}
                </td>
                <td aria-label="actions">
                  <EOREReportActions
                    reportId={data?.id}
                    setLoader={props?.setLoader}
                    setReportsPageList={props?.setReportsPageList}
                    validationStatus={data?.validation_status?.value}
                    isCurrentValidationRole={data?.is_current_validation_role}
                  />
                </td>
              </TableRow>
            );
          })}
          {props?.next && (
              <div colSpan={EORE_HEADERS?.length}>
                <div className="loadMore-div">
                  <button
                    className="loadMore-button"
                    onClick={() => {
                      props?.setReportsPageCount(props?.reportsPageCount + 1);
                    }}
                  >
                    Load more...
                  </button>
                </div>
              </div>
          )}
        </tbody>
      </Table>

      {activeFilter && (
        <FilterDropdown
          key={activeFilter}
          isOpen={true}
          onClose={closeFilter}
          title={EORE_HEADERS?.find(h => h?.key === activeFilter)?.label}
          selectedValues={filters[activeFilter] || []}
          onSelectionChange={(selectedValues) => handleFilterChange(activeFilter, selectedValues)}
          position={filterPosition}
          filterType={activeFilter}
          currentFilters={props?.currentFilters}
          currentDates={props?.currentDates}
        />
      )}
    </div>
  );
};

export const EOREReportActions = (props) => {
  const [disabledDeleteDialogs, setDisabledDeleteDialogs] = useState({});
  const [disabledButtons, setDisabledButtons] = useState({});
  const { user } = useUser();
  const { deleteReportByType } = useReportActions();

  const handleContextMenu = (e) => {
    e.preventDefault();
  };

  const handleDownloadEOREDocxBtn = (itemId) => {
    exportEOREDocx(itemId);
  };

  const handleClick = (id) => {
    setDisabledButtons((prevState) => ({
      ...prevState,
      [id]: { validation: true, edit: true, delete: true },
    }));
    setDisabledDeleteDialogs((prevState) => ({
      ...prevState,
      [id]: true,
    }));
  };

  const handleAuxClick = (event, id) => {
    if (event.button === 1) {
      handleClick(id);
    }
  };

  const draftOrRejectedStatus = props?.validationStatus === 'draft'
    || props?.validationStatus === 'draft_rejected';
  const validationStatus = props?.validationStatus?.startsWith('val_')
    || props?.validationStatus === 'imsma_rejected';

  const isEditDisabled = disabledButtons[props.reportId]?.edit;
  const isValidationDisabled = disabledButtons[props.reportId]?.validation;

  return (
    <div className="all-icons" onContextMenu={handleContextMenu}>
      {user?.permissions?.view_eore_report && (
        <Link
          to={`/reports/eore/view/${props.reportId}`}
          target="_blank"
          rel="noopener noreferrer"
          className="view-icon"
        >
          <ViewSvg />
        </Link>
      )}
      {user?.permissions?.change_eore_report
        && draftOrRejectedStatus
        && (
          <Link
            to={`/reports/eore/edit/${props.reportId}`}
            target="_blank"
            rel="noopener noreferrer"
            className="edit-icon"
            style={{ color: isEditDisabled ? '#d3d3d3' : '#868686', pointerEvents: isEditDisabled ? 'none' : 'auto' }}
            onClick={() => handleClick(props?.reportId)}
            onAuxClick={(event) => handleAuxClick(event, props?.reportId)}
          >
            <PencilSvg />
          </Link>
        )}
      {user?.permissions?.validate_eore_report
        && validationStatus
        && props?.isCurrentValidationRole
        && (
          <Link
            to={`/reports/eore/validation/${props?.reportId}`}
            target="_blank"
            rel="noopener noreferrer"
            className="view-icon"
            style={{ color: isValidationDisabled ? '#d3d3d3' : '#868686', pointerEvents: isValidationDisabled ? 'none' : 'auto' }}
            onClick={() => handleClick(props?.reportId)}
            onAuxClick={(event) => handleAuxClick(event, props?.reportId)}
          >
            <CommentIcon />
          </Link>
        )}
      {user?.permissions?.delete_eore_report
        && draftOrRejectedStatus
        && (
          <DeleteReportDialog
          deleteEoreReport={() =>
            deleteReportByType(props?.reportId, 'eore', props?.setReportsPageList)}
            isDisabled={disabledDeleteDialogs[props?.reportId]}
          />
        )}
      <div className="import-and-export-actions">
        <ExportIcon
          className="export-icon"
          onClick={() => handleDownloadEOREDocxBtn(props?.reportId)}
        />
      </div>
    </div>
  );
};
